import { useCallback } from "react";
import { isEmpty } from 'lodash';
import { GestaoStorageKeys, useGestaoStorage } from "./gestao-storage";
import { TokenGestaoModel } from "../../../model/api/gestao/master";
import { decodeToken } from "react-jwt";
import { UsuarioConectadoModel } from "model/app";

export function useGestaoToken() {
    const { setRegistro, delRegistro, getRegistro } = useGestaoStorage();
    const tokenFromSession: boolean = false;

    const isTokenExpired = useCallback((token: TokenGestaoModel | undefined): boolean => {
        if (!token?.exp) {
            return true;
        }
        return token?.exp * 1000 <= new Date().valueOf();
    }, []);

    const convertToken = useCallback((token?: string): TokenGestaoModel | undefined => {
        let gtoken: TokenGestaoModel | undefined;
        try {
            gtoken = decodeToken(token!) as TokenGestaoModel;
        } catch (e) {
            gtoken = undefined;
        }
        if(gtoken){
            gtoken.usuarioId = gtoken.jti;
            gtoken.originalToken = token || '';
        }
        return gtoken;
    }, []);

    const getTokenFromStorage = useCallback((): TokenGestaoModel | undefined => {
        const token = getRegistro(GestaoStorageKeys.Token, tokenFromSession);
        if (isEmpty(token))
            return undefined;

        return (convertToken(token));
    }, [getRegistro, convertToken, tokenFromSession]);

    const isTokenValid = useCallback((token: TokenGestaoModel | undefined): boolean => {
        return !isEmpty(token) && !isTokenExpired(token);
    }, [isTokenExpired]);

    const persistToken = useCallback((token: string | undefined): void => {
        if (isEmpty(token)) {
            return delRegistro(GestaoStorageKeys.Token, tokenFromSession);
        } else {
            return setRegistro(GestaoStorageKeys.Token, token || "", tokenFromSession);
        }
    }, [delRegistro, setRegistro, tokenFromSession]);

    const getEmpresaAtual = useCallback((): string => {
        const ret = getRegistro(GestaoStorageKeys.EmpresaAtual, tokenFromSession);
        if (isEmpty(ret))
            return '';

        return ret;
    }, [getRegistro, tokenFromSession])

    const persistEmpresaSelecionada = useCallback((empresaId: string | undefined) => {
        if (empresaId === undefined)
            delRegistro(GestaoStorageKeys.EmpresaAtual, tokenFromSession)
        else
            setRegistro(GestaoStorageKeys.EmpresaAtual, empresaId.toString(), tokenFromSession);
    }, [setRegistro, delRegistro, tokenFromSession]);

    const persistTermosDeUso = useCallback((valid: boolean | undefined, statusApi: number | undefined) => {
        if (valid === undefined)
            delRegistro(GestaoStorageKeys.TermosDeUso, tokenFromSession)
        else {
            const termo = {
                termoAceito: valid,
                statusApi
            }
            setRegistro(GestaoStorageKeys.TermosDeUso, termo, tokenFromSession);
        }
    }, [setRegistro, delRegistro, tokenFromSession]);

    const getTermosDeUso = useCallback((): boolean => {
        const ret = getRegistro(GestaoStorageKeys.TermosDeUso, tokenFromSession);
        if (isEmpty(ret)) {
            return true;
        } if (ret.termoAceito === false && ret.statusApi === 400) {
            return false
        } else {
            return true;
        }
    }, [getRegistro, tokenFromSession])

    const removeConnectedUser = useCallback((usuarioId: string) => {
        let conectados: UsuarioConectadoModel[] = getRegistro(GestaoStorageKeys.UsuariosConectados, false) as UsuarioConectadoModel[];
        if (isEmpty(conectados)) {
            conectados = new Array<UsuarioConectadoModel>();
        }
        conectados = conectados.filter(x => x.usuarioId !== usuarioId);
        setRegistro(GestaoStorageKeys.UsuariosConectados, conectados, false);
    }, [setRegistro, getRegistro]);

    const getConnectedUsers = useCallback((): UsuarioConectadoModel[] => {
        let conectados: UsuarioConectadoModel[] = getRegistro(GestaoStorageKeys.UsuariosConectados, false) as UsuarioConectadoModel[];
        if (isEmpty(conectados)) {
            conectados = new Array<UsuarioConectadoModel>();
        }
        return conectados;
    }, [getRegistro]);

    const addConnectedUser = useCallback((token: TokenGestaoModel) => {
        let conectados: UsuarioConectadoModel[] = getRegistro(GestaoStorageKeys.UsuariosConectados, false) as UsuarioConectadoModel[];
        if (isEmpty(conectados)) {
            conectados = new Array<UsuarioConectadoModel>();
        }

        conectados = conectados.filter(x => x.usuarioId !== token.usuarioId);

        let conUser = new UsuarioConectadoModel(token.jti, token.empresa[0].Descricao, token.saudacao, token.originalToken);
        conectados.push(conUser);

        setRegistro(GestaoStorageKeys.UsuariosConectados, conectados, false);
    }, [setRegistro, getRegistro]);


    return {
        isTokenExpired,
        isTokenValid,
        persistToken,
        getTokenFromStorage,
        convertToken,
        getEmpresaAtual,
        persistEmpresaSelecionada,
        getTermosDeUso,
        persistTermosDeUso,
        addConnectedUser,
        removeConnectedUser,
        getConnectedUsers
    };
}